import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Users, CheckCircle, XCircle, UserPlus, Home } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { useHouseholdStore } from '@/stores/household-store'
import { useAuthStore } from '@/stores/auth-store'

export default function HouseholdInvitePage() {
  const { inviteCode } = useParams<{ inviteCode: string }>()
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const { acceptInvite, loading, error, clearError } = useHouseholdStore()
  
  const [status, setStatus] = useState<'pending' | 'accepting' | 'success' | 'error'>('pending')

  useEffect(() => {
    // Clear any previous errors when component mounts
    clearError()
  }, [clearError])

  const handleAcceptInvite = async () => {
    if (!inviteCode || !user) return
    
    setStatus('accepting')
    const success = await acceptInvite(inviteCode)
    
    if (success) {
      setStatus('success')
      setTimeout(() => {
        navigate('/recepten')
      }, 2000)
    } else {
      setStatus('error')
    }
  }

  const handleDecline = () => {
    navigate('/')
  }

  if (!user) {
    return (
      <div className="container max-w-md mx-auto py-16">
        <Card>
          <CardContent className="text-center py-12">
            <UserPlus className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Log eerst in
            </h2>
            <p className="text-gray-600 mb-6">
              Je moet ingelogd zijn om een huishouden uitnodiging te accepteren.
            </p>
            <Button onClick={() => navigate('/login')}>
              Inloggen
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (!inviteCode) {
    return (
      <div className="container max-w-md mx-auto py-16">
        <Card>
          <CardContent className="text-center py-12">
            <XCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Ongeldige uitnodiging
            </h2>
            <p className="text-gray-600 mb-6">
              Deze uitnodigingslink is ongeldig of verlopen.
            </p>
            <Button onClick={() => navigate('/')}>
              Terug naar Home
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="container max-w-md mx-auto py-16">
      <Card>
        <CardHeader className="text-center">
          <div className="mx-auto w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mb-4">
            <Users className="h-6 w-6 text-orange-600" />
          </div>
          <CardTitle className="text-xl">Huishouden Uitnodiging</CardTitle>
        </CardHeader>
        
        <CardContent className="space-y-6">
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center justify-between">
                <p className="text-red-600 text-sm">{error}</p>
                <Button variant="ghost" size="sm" onClick={clearError}>
                  ×
                </Button>
              </div>
            </div>
          )}

          {status === 'pending' && (
            <>
              <div className="text-center">
                <p className="text-gray-600 mb-2">
                  Je bent uitgenodigd om lid te worden van een huishouden.
                </p>
                <p className="text-sm text-gray-500">
                  Door lid te worden kun je recepten delen met andere leden van het huishouden.
                </p>
              </div>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={handleDecline}
                  className="flex-1"
                >
                  Afwijzen
                </Button>
                <Button
                  onClick={handleAcceptInvite}
                  disabled={loading}
                  className="flex-1"
                >
                  {loading ? 'Accepteren...' : 'Accepteren'}
                </Button>
              </div>
            </>
          )}

          {status === 'accepting' && (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Uitnodiging accepteren...</p>
            </div>
          )}

          {status === 'success' && (
            <div className="text-center py-8">
              <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Uitnodiging geaccepteerd!
              </h3>
              <p className="text-gray-600 mb-4">
                Je bent nu lid van het huishouden. Je wordt doorgestuurd naar je recepten.
              </p>
              <div className="animate-pulse text-sm text-gray-500">
                Doorsturen...
              </div>
            </div>
          )}

          {status === 'error' && (
            <div className="text-center py-8">
              <XCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Fout bij accepteren
              </h3>
              <p className="text-gray-600 mb-6">
                Er is een fout opgetreden bij het accepteren van de uitnodiging.
              </p>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => navigate('/')}
                  className="flex-1"
                >
                  <Home className="h-4 w-4 mr-2" />
                  Naar Home
                </Button>
                <Button
                  onClick={() => setStatus('pending')}
                  className="flex-1"
                >
                  Opnieuw Proberen
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}